import type { FormEvent } from 'react';

import { DataExplorerToolbarActions } from './DataExplorerToolbarActions';
import { useDataExplorerAppearance } from './appearanceContext';
import { resolveDataExplorerAppearance } from './theme';
import type { DataExplorerToolbarShellProps } from './types';
import { cn } from './utils';

function DefaultSearchIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function DataExplorerToolbarShell<TContext = unknown>({
  rootRef,
  dataAttributes,
  context,
  leading,
  leadingActions,
  searchVisible = true,
  searchValue = '',
  searchPlaceholder = 'Search data...',
  searchIcon,
  viewOptions = [],
  activeViewId,
  actions,
  toolbarActions,
  appearance,
  children,
  className,
  classNames,
  onSearchChange,
  onSearchSubmit,
  onViewChange,
}: DataExplorerToolbarShellProps<TContext>) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearchSubmit?.(searchValue);
  };
  const resolvedAppearance = useDataExplorerAppearance(appearance);
  const theme = resolveDataExplorerAppearance(resolvedAppearance);

  return (
    <div
      ref={rootRef}
      {...dataAttributes}
      className={cn(
        theme.slots.toolbarRoot,
        classNames?.root,
        className,
      )}
      style={theme.styles.toolbarRoot}
    >
      {leading || leadingActions?.length ? (
        <div className={cn(theme.slots.toolbarLeading, classNames?.leading)}>
          {leading}
          {leadingActions?.length ? (
            <DataExplorerToolbarActions
              context={context}
              actions={leadingActions}
              layout="inline"
              appearance={resolvedAppearance}
              classNames={{
                action: classNames?.action,
                menuContent: classNames?.menuContent,
                menuItem: classNames?.menuItem,
              }}
            />
          ) : null}
        </div>
      ) : null}

      {searchVisible ? (
        <form
          className={cn(
            theme.slots.toolbarSearch,
            classNames?.search,
          )}
          style={theme.styles.toolbarSearch}
          onSubmit={handleSubmit}
        >
          <span
            className={cn(
              theme.slots.toolbarSearchIcon,
              classNames?.searchIcon,
            )}
          >
            {searchIcon ?? <DefaultSearchIcon />}
          </span>
          <input
            value={searchValue}
            placeholder={searchPlaceholder}
            className={cn(
              theme.slots.toolbarSearchInput,
              classNames?.searchInput,
            )}
            onChange={(event) => onSearchChange?.(event.currentTarget.value)}
          />
        </form>
      ) : null}

      {viewOptions.length > 0 ? (
        <div
          className={cn(
            theme.slots.toolbarViewSwitch,
            classNames?.viewSwitch,
          )}
          style={theme.styles.toolbarViewSwitch}
        >
          {viewOptions.map((option) => {
            const active = option.id === activeViewId;
            return (
              <button
                key={option.id}
                type="button"
                disabled={option.disabled}
                className={cn(
                  theme.slots.toolbarViewButton,
                  active && theme.slots.toolbarViewButtonActive,
                  classNames?.viewButton,
                )}
                onClick={() => onViewChange?.(option.id)}
              >
                {option.icon}
                {option.label ? <span>{option.label}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {toolbarActions?.length ? (
        <DataExplorerToolbarActions
          context={context}
          actions={toolbarActions}
          layout="toolbar"
          appearance={appearance}
          classNames={{
            root: classNames?.actions,
            action: classNames?.action,
            menuContent: classNames?.menuContent,
            menuItem: classNames?.menuItem,
          }}
        />
      ) : actions || children ? (
        <div className={cn(theme.slots.toolbarActions, classNames?.actions)}>
          {actions}
          {children}
        </div>
      ) : null}
    </div>
  );
}
